/**
 * Tenant-facing alerts (PLAN §11.1, D27; phase 7b item 4): the alerts a tenant
 * has now, their history, and who is told — the notification settings and
 * channels (email to users and groups, a Slack incoming webhook, a generic
 * HMAC-signed webhook).
 *
 * The alert types, severities and the rules' semantics are the pure core's
 * (`@acp/tenant-alerts`); this module is their wire shape. Cloud only: a node
 * has no alerts of its own.
 *
 * **Secrets.** A Slack hook URL IS a credential (whoever holds it posts to the
 * channel), and so is a webhook's signing secret. Both are written once and
 * stored in Secrets Manager under the tenant's path; no answer ever carries
 * them — a channel shows `target`, a hint (the host, and a Slack hook's last
 * four characters), and `hasSecret`.
 */
import { ulidSchema } from '@acp/domain/primitives.js';
import { ALERT_TYPES, SEVERITIES } from '@acp/tenant-alerts/catalog.js';
import { z } from 'zod';
import { siteIdSchema, studioIdSchema } from './studios.js';
import { entityIdSchema, userIdSchema } from './tenancy.js';

const groupIdSchema = entityIdSchema;

const isoSchema = z.iso.datetime({ offset: true });

export const alertTypeSchema = z.enum(ALERT_TYPES);
export const severitySchema = z.enum(SEVERITIES);

/** An alert's identity: `<type>:<subject ids>` (`cert-expiring:<site>/<node>`, `device-disconnected-on-air:<production>/<device>`). */
export const alertKeySchema = z.string().regex(/^[a-z-]{1,40}:[A-Za-z0-9_.:/-]{1,300}$/, 'an alert key: `<type>:<ids>`');

const detailSchema = z.record(z.string().max(64), z.union([z.string().max(2000), z.number(), z.null()]));

export const alertSchema = z
  .object({
    key: alertKeySchema,
    type: alertTypeSchema,
    severity: severitySchema,
    /** `active` while a condition holds; an event, and a condition that stopped, are `resolved`. */
    status: z.enum(['active', 'resolved']),
    siteId: siteIdSchema,
    studioId: studioIdSchema.nullable(),
    subject: z.string().max(500),
    summary: z.string().max(1000),
    detail: detailSchema,
    raisedAt: isoSchema,
    resolvedAt: isoSchema.nullable(),
    /** How many times it was raised (a condition's episodes, an event's occurrences). */
    occurrences: z.number().int().positive(),
    /** When people were last notified about it; null if never. */
    notifiedAt: isoSchema.nullable(),
  })
  .strict();
export type Alert = z.infer<typeof alertSchema>;

export const listAlertsQuerySchema = z
  .object({
    status: z.enum(['active', 'all']).optional().describe('`active` (the default): the conditions that hold now. `all`: every alert the tenant has a state for, the recent events included.'),
  })
  .strict();

export const alertCountsSchema = z.object({ info: z.number().int().nonnegative(), warning: z.number().int().nonnegative(), critical: z.number().int().nonnegative() }).strict();
export type AlertCounts = z.infer<typeof alertCountsSchema>;

export const listAlertsResponseSchema = z
  .object({
    alerts: z.array(alertSchema),
    /** The ACTIVE alerts the caller may see, per severity (the top bar's badge). */
    counts: alertCountsSchema,
  })
  .strict();

export const alertTransitionSchema = z.enum(['raised', 'resolved', 'occurred']);

export const alertHistoryEntrySchema = z
  .object({
    entryId: ulidSchema,
    key: alertKeySchema,
    type: alertTypeSchema,
    transition: alertTransitionSchema,
    severity: severitySchema,
    siteId: siteIdSchema,
    studioId: studioIdSchema.nullable(),
    subject: z.string().max(500),
    summary: z.string().max(1000),
    at: isoSchema,
    /** The channels it was sent to; empty when nobody was notified (the cooldown, the type off, no channel). */
    channelIds: z.array(entityIdSchema),
  })
  .strict();
export type AlertHistoryEntry = z.infer<typeof alertHistoryEntrySchema>;

export const MAX_HISTORY_PAGE = 200;

export const listAlertHistoryQuerySchema = z
  .object({
    cursor: z.string().max(512).optional().describe('`nextCursor` of the previous page.'),
    limit: z.coerce.number().int().min(1).max(MAX_HISTORY_PAGE).optional().describe(`At most this many entries (default 50, at most ${String(MAX_HISTORY_PAGE)}).`),
    type: alertTypeSchema.optional().describe('Only entries of this alert type.'),
  })
  .strict();

export const listAlertHistoryResponseSchema = z.object({ entries: z.array(alertHistoryEntrySchema), nextCursor: z.string().nullable() }).strict();

// ─── settings ───

export const alertRuleSchema = z.object({ enabled: z.boolean(), severity: severitySchema }).strict();
/** One rule per alert type, all of them. */
export const alertRulesSchema = z.record(alertTypeSchema, alertRuleSchema);
export const ruleOverrideSchema = z.object({ enabled: z.boolean().optional(), severity: severitySchema.optional() }).strict();
export const siteOverrideSchema = z.object({ siteId: siteIdSchema, rules: z.partialRecord(alertTypeSchema, ruleOverrideSchema) }).strict();

export const MAX_CHANNELS = 20;
export const MAX_RECIPIENTS = 50;

export const channelKindSchema = z.enum(['email', 'slack', 'webhook']);
export type ChannelKind = z.infer<typeof channelKindSchema>;

export const recipientsSchema = z
  .object({ userIds: z.array(userIdSchema).max(MAX_RECIPIENTS), groupIds: z.array(groupIdSchema).max(MAX_RECIPIENTS) })
  .strict()
  .refine((r) => r.userIds.length + r.groupIds.length >= 1, { message: 'name at least one user or group' });

export const alertChannelSchema = z
  .object({
    channelId: entityIdSchema,
    name: z.string().min(1).max(100),
    kind: channelKindSchema,
    enabled: z.boolean(),
    /** The lowest severity it is sent. */
    minSeverity: severitySchema,
    /** The sites whose alerts it gets; null: every site. */
    siteIds: z.array(siteIdSchema).nullable(),
    /** Email only: who gets it. */
    recipients: z.object({ userIds: z.array(userIdSchema), groupIds: z.array(groupIdSchema) }).strict().nullable(),
    /** Where it goes, as a hint (never the URL itself): `hooks.slack.com …Ab3x`, `alerts.example.com`. Null for email. */
    target: z.string().max(200).nullable(),
    /** Slack and webhook: whether its URL (and signing secret) is stored. */
    hasSecret: z.boolean(),
    createdAt: isoSchema,
    updatedAt: isoSchema,
  })
  .strict();
export type AlertChannel = z.infer<typeof alertChannelSchema>;

export const alertSettingsSchema = z
  .object({
    /** Optimistic concurrency for `PUT /alerts/settings` (0 before the first save). */
    version: z.number().int().nonnegative(),
    /** The shortest interval between two notifications of one alert. */
    cooldownMinutes: z.number().int().min(1).max(1440),
    rules: alertRulesSchema,
    siteOverrides: z.array(siteOverrideSchema).max(200),
    channels: z.array(alertChannelSchema).max(MAX_CHANNELS),
    updatedAt: isoSchema.nullable(),
    updatedBy: z.string().max(320).nullable(),
  })
  .strict();
export type AlertSettings = z.infer<typeof alertSettingsSchema>;

export const alertSettingsResponseSchema = z.object({ settings: alertSettingsSchema }).strict();

export const updateAlertSettingsRequestSchema = z
  .object({
    version: z.number().int().nonnegative().describe('The version this edit started from; another save since is `conflict`.'),
    cooldownMinutes: z.number().int().min(1).max(1440),
    rules: alertRulesSchema,
    siteOverrides: z.array(siteOverrideSchema).max(200),
  })
  .strict();
export type UpdateAlertSettingsRequest = z.infer<typeof updateAlertSettingsRequestSchema>;

/**
 * The host of an https URL (lowercased, port dropped), or null when it is not one this contract accepts: userinfo
 * (`user:pass@`) or an IPv6 literal. Parsed by pattern — the contract has no host APIs (no `URL` global here).
 */
export function channelUrlHost(raw: string): string | null {
  const match = /^https:\/\/([^/?#]+)(?:[/?#]|$)/i.exec(raw);
  const authority = match?.[1];
  if (authority === undefined || authority.includes('@') || authority.startsWith('[')) return null;
  const host = authority.replace(/:\d{1,5}$/, '').toLowerCase();
  return /^[a-z0-9.-]+$/.test(host) ? host : null;
}

/**
 * A URL a channel may post to (the cloud posts to it, so it must not aim inward): https, a public-looking host
 * NAME — no IP literal, no `localhost`, `.local` or `.internal`, at least one dot — and no credentials in it.
 */
export const channelUrlSchema = z
  .url({ protocol: /^https$/ })
  .max(2000)
  .refine(
    (raw) => {
      const host = channelUrlHost(raw);
      if (host === null) return false;
      const ipLiteral = /^\d{1,3}(\.\d{1,3}){3}$/.test(host);
      return !ipLiteral && host !== 'localhost' && !host.endsWith('.localhost') && !host.endsWith('.local') && !host.endsWith('.internal') && host.includes('.') && !host.endsWith('.');
    },
    { message: 'an https URL to a public host name (no IP address, localhost or credentials)' },
  );

export const SLACK_HOOK_PREFIX = 'https://hooks.slack.com/';
export const MIN_SIGNING_SECRET_CHARS = 16;

export const createAlertChannelRequestSchema = z
  .object({
    channelId: entityIdSchema.optional().describe('Makes a retry idempotent: a channel with this id is returned as it is.'),
    name: z.string().min(1).max(100),
    kind: channelKindSchema,
    enabled: z.boolean().optional(),
    minSeverity: severitySchema,
    siteIds: z.array(siteIdSchema).max(200).nullable(),
    recipients: recipientsSchema.nullable().optional(),
    url: channelUrlSchema.nullable().optional(),
    signingSecret: z.string().min(MIN_SIGNING_SECRET_CHARS).max(256).nullable().optional(),
  })
  .strict()
  .superRefine((body, ctx) => {
    for (const [path, message] of channelProblems(body)) ctx.addIssue({ code: 'custom', path: [path], message });
  });
export type CreateAlertChannelRequest = z.infer<typeof createAlertChannelRequestSchema>;

type ChannelFields = { kind: ChannelKind; recipients?: unknown; url?: string | null | undefined; signingSecret?: string | null | undefined };

function isSet<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

/** What a new channel of its kind lacks or must not have: `[field, message]` pairs. */
export function channelProblems(body: ChannelFields): [string, string][] {
  const problems: [string, string][] = [];
  if (body.kind === 'email') {
    if (!isSet(body.recipients)) problems.push(['recipients', 'an email channel names its recipients']);
    if (isSet(body.url)) problems.push(['url', 'an email channel has no URL']);
    return problems;
  }
  if (!isSet(body.url)) problems.push(['url', `a ${body.kind} channel needs its URL`]);
  else if (body.kind === 'slack' && !body.url.startsWith(SLACK_HOOK_PREFIX)) problems.push(['url', `a Slack incoming webhook URL starts ${SLACK_HOOK_PREFIX}`]);
  if (body.kind === 'webhook' && !isSet(body.signingSecret)) problems.push(['signingSecret', 'a webhook channel needs a signing secret (the receiver verifies the HMAC with it)']);
  if (body.kind === 'slack' && isSet(body.signingSecret)) problems.push(['signingSecret', 'a Slack channel is not signed']);
  if (isSet(body.recipients)) problems.push(['recipients', 'only an email channel has recipients']);
  return problems;
}

/** What a PATCH may change; the kind is fixed. A new `url` (or `signingSecret`) replaces the stored one. */
export const updateAlertChannelRequestSchema = z
  .object({
    name: z.string().min(1).max(100).optional(),
    enabled: z.boolean().optional(),
    minSeverity: severitySchema.optional(),
    siteIds: z.array(siteIdSchema).max(200).nullable().optional(),
    recipients: recipientsSchema.optional(),
    url: channelUrlSchema.optional(),
    signingSecret: z.string().min(MIN_SIGNING_SECRET_CHARS).max(256).optional(),
  })
  .strict();
export type UpdateAlertChannelRequest = z.infer<typeof updateAlertChannelRequestSchema>;

export const alertChannelPathSchema = z.object({ channelId: entityIdSchema.describe('The channel’s id.') }).strict();
export const alertChannelResponseSchema = z.object({ channel: alertChannelSchema }).strict();

export const testAlertRequestSchema = z
  .object({
    channelId: entityIdSchema.nullable().describe('The channel to test; null tests every enabled channel.'),
    type: alertTypeSchema.optional().describe('The alert type the test message pretends to be (default `failover`).'),
  })
  .strict();

export const testAlertResultSchema = z.object({ channelId: entityIdSchema, ok: z.boolean(), detail: z.string().max(500) }).strict();
export const testAlertResponseSchema = z.object({ results: z.array(testAlertResultSchema) }).strict();
export type TestAlertResult = z.infer<typeof testAlertResultSchema>;
